import React, { FunctionComponent, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Header } from '@lightspeed/design-system-react'
import { createSelector } from '@reduxjs/toolkit'
import { round } from 'vend-number'
import { getCurrencyFormatParts } from '@vend/utilities'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { RootState } from '../../../../state/store'
import { isReturn } from '../../../../functions/sale'
import { hasKlarnaPayments } from '../../../../functions/payment-types/klarna'
import { useAmountTenderedMessages } from './useAmountTenderedMessages'
import { usePaymentHeading } from './usePaymentHeading'

export interface AmountTenderedProps {
  amountTendered: string
  onChange: (amountTendered: string) => void
  setHasError: (hasError: boolean) => void
  /** Space for the absolutely positioned legacy cash discounting banner. */
  reserveCashDiscountBannerSpace?: boolean
}

const SYSTEM_DECIMAL_SEPARATOR = '.'

const dataSelector = createSelector(
  [
    (state: RootState) => state.currentSale.originalSalePayments,
    (state: RootState) => state.currentSale.returnFor,
  ],
  (originalSalePayments, returnFor) => ({
    originalSalePayments,
    returnFor,
  })
)

const formatDisplayValue = (decimalSeparator: string, value: string) => {
  // We don't need to convert separators when `decimalSeparator` = `systemDecimalSeparator`.
  if (decimalSeparator === SYSTEM_DECIMAL_SEPARATOR) {
    return value
  }

  return Number.isFinite(Number(value))
    ? value.replace(
        new RegExp(`\\${SYSTEM_DECIMAL_SEPARATOR}`, 'g'),
        decimalSeparator
      )
    : value
}

const formatInputValue = (decimalSeparator: string, value: string) => {
  // We don't need to convert separators when `decimalSeparator` != `SYSTEM_DECIMAL_SEPARATOR`.
  if (decimalSeparator === SYSTEM_DECIMAL_SEPARATOR) {
    return value
  }

  return value.replace(
    new RegExp(`\\${decimalSeparator}`, 'g'),
    SYSTEM_DECIMAL_SEPARATOR
  )
}

export const AmountTendered: FunctionComponent<AmountTenderedProps> = ({
  amountTendered,
  onChange,
  setHasError,
  reserveCashDiscountBannerSpace = false,
}) => {
  const retailer = useRetailerSettingsManager().get()
  const registerManager = useRegisterManager()
  const activeOutlet = registerManager.getActiveOutlet()
  const heading = usePaymentHeading()
  const saleData = useAppSelector(dataSelector)
  const { error, message } = useAmountTenderedMessages(amountTendered, onChange)
  const [hasSelected, setHasSelected] = useState(false)

  const decimalSeparator = getCurrencyFormatParts(
    retailer.culture,
    activeOutlet.currency,
    activeOutlet.currency_symbol
  ).decimal

  const shouldDisableAmountTendered =
    isReturn(saleData) &&
    hasKlarnaPayments({ payments: saleData.originalSalePayments })

  const onBlur = () => {
    // If the input is empty, do not format it to "0.00". Number('') will return 0 bellow
    // Let it remain empty so the "Please enter an amount" error can be shown.
    if (amountTendered.trim() === '') {
      // Ensure the error state is updated if it wasn't already (e.g., if user typed then deleted)
      // The `useAmountTenderedMessages` hook will provide the error for an empty string.
      // The useEffect below will call setHasError based on the `error` from the hook.
      return
    }

    if (!isNaN(Number(amountTendered))) {
      onChange(round(amountTendered, 2))
    }
  }

  const selectInput = (el: HTMLInputElement | null) => {
    if (el && !hasSelected) {
      el.select()
      setHasSelected(true)
    }
  }

  useEffect(() => {
    setHasError(Boolean(error))
  }, [error, setHasError])

  // Prevent form submission on enter key
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const formattedValue = formatInputValue(decimalSeparator, inputValue)

    const isValid = !isNaN(Number(formattedValue))
    setHasError(!isValid)

    onChange(formattedValue)
  }

  return (
    <form name="amount-tendered" className="payment-form-container">
      <div
        className={classNames('payment-form', {
          'payment-form-with-cash-discounting': reserveCashDiscountBannerSpace,
        })}
      >
        <Header type="hero" level="2" className="payment-header">
          {heading}
        </Header>
        <input
          value={formatDisplayValue(decimalSeparator, amountTendered)}
          onChange={e => handleInputChange(e)}
          className={classNames('vd-input wr-input--amount-tendered', {
            'vd-input--error': error,
          })}
          disabled={shouldDisableAmountTendered}
          inputMode="decimal"
          aria-label="Amount tendered"
          autoComplete="off"
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          ref={selectInput}
          data-testid="amount-tendered-input"
        />
      </div>
      <div className="vd-pt2 payment-form-messages vd-text-supplementary">
        {error || message}
      </div>
    </form>
  )
}
